import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Card, CircularProgress, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { Formik } from "formik";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import { CONTACT_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetContacts from "@/hooks/requests/contact/use-get-contacts";
import { useButtonPermission } from "@/hooks/use-button-permission";
import useFilters from "@/hooks/use-filters";
import usePagination from "@/hooks/use-pagination";
import IContact from "@/interfaces/contact.interface";
import { ContactsMessages } from "@/messages/contacts/contacts.messages";

import ContactFiltersForm from "./components/ContactFiltersForm";
import { ContactsImportModal } from "./components/ContactsImportModal";
import { ContactTable } from "./components/ContactTable";
import { initialFilterValues } from "./constants";
import { ContactSortField, SortDirection } from "./types";

type Props = { messages: ContactsMessages };

const FIRST_PAGE = 0;

export default function ContactsPage({ messages }: Props) {
  const { lang } = useLanguage();
  const contactPermission = useButtonPermission(CONTACT_PERMISSIONS.CREATE);
  const pagination = usePagination();
  const { pageNumber, pageSize, changePage } = pagination;
  const router = useNavigate();
  const { filters, changeFilters } = useFilters<IContact>({ initialValue: initialFilterValues });
  const [sortField, setSortField] = useState<ContactSortField>("firstName");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const { data: contacts, isLoading, error } = useGetContacts({
    filters,
    pageNumber,
    pageSize,
    sortField,
    sortDirection,
  });
  const [openImportContactsModal, setOpenImportContactsModal] = useState(false);

  const handleOpenImportContactsModal = () => {
    setOpenImportContactsModal(true);
  };

  const handleCloseImportContactsModal = () => {
    setOpenImportContactsModal(false);
  };

  const hasItems = !!contacts && contacts.items.length > 0;
  const noItems = !!contacts && contacts.items.length === 0;

  const shouldLoadContactsTable = !!contacts && !isLoading && !error && hasItems;
  const shouldLoadError = !contacts && !isLoading && !!error;
  const noFound = (!!contacts && !isLoading && !error && noItems) || typeof contacts === "string";

  return (
    <PageLayout messages={messages} title={messages.contactsPageTitle}>
      <PageContainer>
        <Formik
          initialValues={filters}
          validateOnChange={false}
          onSubmit={(submittedFilters) => {
            changePage(FIRST_PAGE);
            changeFilters(submittedFilters);
          }}
        >
          <ContactFiltersForm messages={messages} />
        </Formik>

        <Card sx={({ functions: { pxToRem } }) => ({ marginTop: pxToRem(16) })}>
          <Suspense isSuspended={isLoading} fallback={<CircularProgress size={50} />}>
            <MDBox p={2}>
              <MDBox
                p={2}
                display="flex"
                flexWrap="wrap"
                gap={2}
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" sx={{ flex: "1 1 auto" }}>
                  {messages.contactsPageTitle}
                </MDTypography>

                <MDBox display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel shrink>{messages.sortByLabel}</InputLabel>
                    <Select
                      value={sortField}
                      label={messages.sortByLabel}
                      onChange={(e) => {
                        setSortField(e.target.value as ContactSortField);
                        changePage(FIRST_PAGE);
                      }}
                    >
                      <MenuItem value="firstName">{messages.sortByFirstName}</MenuItem>
                      <MenuItem value="addressCity">{messages.sortByCity}</MenuItem>
                      <MenuItem value="addressState">{messages.sortByState}</MenuItem>
                      <MenuItem value="active">{messages.sortByActive}</MenuItem>
                      <MenuItem value="consentStatus">{messages.sortByConsent}</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel shrink>{messages.sortDirectionLabel}</InputLabel>
                    <Select
                      value={sortDirection}
                      label={messages.sortDirectionLabel}
                      onChange={(e) => {
                        setSortDirection(e.target.value as SortDirection);
                        changePage(FIRST_PAGE);
                      }}
                    >
                      <MenuItem value="asc">{messages.sortDirectionAsc}</MenuItem>
                      <MenuItem value="desc">{messages.sortDirectionDesc}</MenuItem>
                    </Select>
                  </FormControl>
                </MDBox>

                <MDBox display="flex" gap={2}>
                  <MDButton variant="gradient" color="dark" onClick={handleOpenImportContactsModal}>
                    {messages.importContactsButton}
                  </MDButton>

                  <MDButton
                    variant="gradient"
                    color="dark"
                    onClick={() => router(ROUTE_URLS.createContact(lang))}
                    disabled={contactPermission.disabled}
                    isLoading={contactPermission.loading}
                    endIcon={<AddCircleOutlineIcon />}
                  >
                    {messages.addContactButton}
                  </MDButton>
                </MDBox>
              </MDBox>

              {!!shouldLoadContactsTable && (
                <ContactTable pagination={pagination} data={contacts} messages={messages} />
              )}
              <ContactsImportModal
                messages={messages}
                open={openImportContactsModal}
                onClose={handleCloseImportContactsModal}
              />

              {!!shouldLoadError && (
                <AlertBlock
                  title={messages.contactErrorToLoad}
                  description={error.message}
                  alt={messages.contactNotFoundAlt}
                />
              )}

              {!!noFound && (
                <AlertBlock
                  title={messages.contactNotFoundError}
                  description={messages.contactNotFoundDescription}
                  alt={messages.contactNotFoundAlt}
                />
              )}
            </MDBox>
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}
